import React, { Dispatch, useEffect } from 'react'
import {
  Control,
  Controller,
  UseFormGetValues,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form'

import s from './styles.module.scss'

import { MinMaxInputFields } from '../min-max-input-fields/MinMaxInputFields'

type RangeSliderProps = {
  minLimit?: number
  maxLimit?: number
  initialMin?: number
  initialMax?: number
  step?: number
  control: Control<any, any>
  getValues: UseFormGetValues<any>
  watch: UseFormWatch<any>
  setValue: UseFormSetValue<any>
  minName: string
  maxName: string
  minUnit?: string
  maxUnit?: string
  tempValueMin: number
  tempValueMax: number
  setTransientValueMin: Dispatch<React.SetStateAction<number>>
  setTransientValueMax: Dispatch<React.SetStateAction<number>>
}

export const RangeSlider: React.FC<RangeSliderProps> = ({
  initialMin = 0,
  initialMax = 10,
  minLimit = 0,
  maxLimit = 100,
  step = 1,
  control,
  getValues,
  watch,
  minName,
  maxName,
  minUnit,
  maxUnit,
  setValue,
  tempValueMin,
  tempValueMax,
  setTransientValueMin,
  setTransientValueMax,
}) => {
  const sliderMinStepRange = Math.round(maxLimit * 0.06)

  const handleSliderChange = (value: number, isMin: boolean) => {
    if (isMin) {
      if (value + sliderMinStepRange <= tempValueMax) {
        setTransientValueMin(value)
      }
    } else {
      if (value - sliderMinStepRange >= tempValueMin) {
        setTransientValueMax(value)
      }
    }
  }

  const applySliderChange = (isMin: boolean) => {
    if (isMin) {
      setValue(minName, tempValueMin ?? minLimit, {
        shouldValidate: true,
        shouldDirty: true,
      })
    } else {
      setValue(maxName, tempValueMax ?? maxLimit, {
        shouldValidate: true,
        shouldDirty: true,
      })
    }
  }

  const progressLeft = ((tempValueMin - minLimit) / (maxLimit - minLimit)) * 100
  const progressRight =
    100 - ((tempValueMax - minLimit) / (maxLimit - minLimit)) * 100

  useEffect(() => {
    if (tempValueMin < minLimit) {
      setTransientValueMin(minLimit)
    } else if (tempValueMin > maxLimit) {
      setTransientValueMin(maxLimit)
    }
  }, [tempValueMin])

  useEffect(() => {
    if (tempValueMax < minLimit) {
      setTransientValueMax(minLimit)
    } else if (tempValueMax > maxLimit) {
      setTransientValueMax(maxLimit)
    }
  }, [tempValueMax])

  return (
    <div className={s.container}>
      <MinMaxInputFields
        control={control}
        minName={minName}
        maxName={maxName}
        initialMin={String(initialMin)}
        initialMax={String(initialMax)}
        minLimit={minLimit}
        maxLimit={maxLimit}
        getValues={getValues}
        watch={watch}
        minUnit={minUnit}
        maxUnit={maxUnit}
        minValue={tempValueMin ?? minLimit}
        maxValue={tempValueMax ?? maxLimit}
        minStepRange={sliderMinStepRange}
        allowEmptyField={false}
        onInputChangeCallback={(value, isMin) => {
          if (isMin) {
            setTransientValueMin(value)
          } else {
            setTransientValueMax(value)
          }
        }}
      />
      <div className={s.slider}>
        <div
          className={s.progress}
          style={{
            left: `${progressLeft}%`,
            right: `${progressRight}%`,
          }}
        ></div>
      </div>
      <div className={s.rangeInput}>
        <Controller
          control={control}
          name={minName}
          render={() => (
            <input
              type='range'
              min={minLimit}
              max={maxLimit}
              value={tempValueMin}
              onChange={(e) => handleSliderChange(Number(e.target.value), true)}
              step={step}
              onMouseUp={() => applySliderChange(true)}
              onTouchEnd={() => applySliderChange(true)}
            />
          )}
        />
        <Controller
          control={control}
          name={maxName}
          render={() => (
            <input
              type='range'
              min={minLimit}
              max={maxLimit}
              value={tempValueMax}
              onChange={(e) =>
                handleSliderChange(Number(e.target.value), false)
              }
              step={step}
              onMouseUp={() => applySliderChange(false)}
              onTouchEnd={() => applySliderChange(false)}
            />
          )}
        />
      </div>
    </div>
  )
}
